import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { generatePath } from 'react-router-dom'
import { NameDto } from '@/api/Common/Types'
import {
  IntroducingBrokerCommissionPlanGroupDto,
  TradingAccountType,
} from '@/api/Trading-Accounts/Types/IntroducingBrokerCommissionPlanGroupDto'
import { IntroducingBrokerLoyaltyDto } from '@/api/Trading-Accounts/Types/IntroducingBrokerLoyaltyDto'
import { PlatformTypeDto } from '@/api/Trading-Accounts/Types/PlatformTypeDto'
import { CacheLabel } from '@/components/CacheLabel'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import {
  FormCheckboxField,
  FormDateField,
  FormField,
  FormMultiSelectField,
  FormReadonlyField,
  FormSelectField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import { Dialog } from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Spinner } from '@/components/ui/spinner'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { formatLargeNumber, formatNumberWithCommas } from '@/utils/formatNumber'
import { GridColumn as Column, Grid, GridCellProps } from '@progress/kendo-react-grid'
import { ClientDetailedDto } from 'api/Clients/Types/ClientDetailedDto'
import { TickmillCompany } from 'api/Clients/Types/TickmillCompany'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { isUserStatusLocked } from 'api/Identity/Types/UserResponse'
import { PageData } from 'api/Pagination/Types'
import {
  StandardCommission,
  StandardCommissionDto,
} from 'api/Trading-Accounts/Types/IntroducingBrokerDetailedProfileDto'
import {
  AdjustNotionalValue,
  Client,
  IBProfile,
  IntroducingBrokerDetailsDto,
  IntroducingBrokerRestrictions,
  IntroducingBrokerStatus,
  LoyaltyDetails,
} from 'api/Trading-Accounts/Types/IntroducingBrokerDetailsDto'
import { UpdateIntroducingBroker } from 'api/Trading-Accounts/Types/UpdateIntroducingBroker'
import AddButton from 'components/AddButton/AddButton'
import MoreButton from 'components/MoreButton/MoreButton'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { createGridState, useGridFilter, useGridState, useGridStatusState } from 'features/DataGrid'
import { FormikProps, withFormik } from 'formik'
import { isGoBack } from 'lib/react-router-dom'
import { ChevronDown, CircleAlert, Info, ScrollText } from 'lucide-react'
import { useAppDispatch, useAppSelector } from 'store'
import { CompositeFilterDescriptor, GridFilterActionState } from 'store/grid'
import { fetchAdministrationUsersAction } from 'store/identity/identity.actions'
import {
  fetchBonusNvAdjustmentReasons,
  fetchIntroducingBrokerByTickmillCompany,
  fetchIntroducingBrokerCalculationStatusesAction,
  fetchIntroducingBrokerParentTypesAction,
  fetchIntroducingBrokerProfileAccountTypeAction,
  fetchIntroducingBrokerProfilesByTickmillCompany,
  fetchIntroducingBrokerStatusesAction,
  fetchRebateCalcMethodTypesAction,
} from 'store/partnerships/partnerships.actions'
import {
  fetchInstrumentsAction,
  fetchPlatformTypesAction,
  fetchTradingAccountRestrictions,
} from 'store/trading-accounts/trading-accounts.actions'
import { MainIBSchemeEnum } from 'types/MainIBSchemeEnum'
import { useApiClient } from 'utils/api/ApiClient'
import { IdentityApiClient } from 'utils/api/identityApi'
import { PartnershipsApiClient } from 'utils/api/partnershipsApi'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { AuthSessionContext } from 'utils/AuthContext'
import { INTRODUCING_BROKERS_CACHE_MESSAGE } from 'utils/cache'
import { formatLocalDateUTC, getDateTimeFormat } from 'utils/date'
import { links } from 'utils/featureToggles'
import { FormSubmitValues } from 'utils/formValidation'
import { isEmpty } from 'utils/is-empty'
import { hasPermission } from 'utils/permissionsValidator'
import { buildQuery } from 'utils/QueryHelpers'
import { TableStateContextData } from 'utils/TableStateContext'

import ClientAreaAuditLogModal from '../../../components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import { InstrumentCommissionModal } from './InstrumentComissionModal'
import { InstrumentCommissionsGrid } from './InstrumentCommissionsGrid'
import { IntroducingBrokerLoyaltyProgramForm } from './IntroducingBrokerLoyaltyProgramForm'
import {
  AddEditIntroducingBrokerCommissionPlanGroupDtoFormValues,
  IntroducingBrokerRebateFormModal,
} from './IntroducingBrokerRebateFormModal'
import {
  IntroducingBrokerNavigation,
  IntroducingBrokerTab,
} from './Navigation/IntroducingBrokerNavigation'

const LOYALTY_PROGRAM_RULES_URL =
  'https://d1fyjtrsl71uh.cloudfront.net/collections/campaign-tc/ib-loyalty/ib-loyalty-tc-en.pdf'
const CAMPAIGN_RESTRICTIONS_FIELD = [30, 31]

const InfoTooltip: React.FC<{
  children: React.ReactNode
  side?: React.ComponentProps<typeof TooltipContent>['side']
}> = ({ children, side = 'right' }) => (
  <Tooltip>
    <TooltipTrigger>
      <Info size={14} style={{ marginLeft: '0.25rem' }} />
    </TooltipTrigger>
    <TooltipContent side={side}>{children}</TooltipContent>
  </Tooltip>
)

const DetailFieldLabel: React.FC<{
  label: React.ReactNode
  tooltip?: React.ReactNode
}> = ({ label, tooltip }) => (
  <span className='inline-flex items-center'>
    <span>{label}</span>
    {tooltip && <InfoTooltip>{tooltip}</InfoTooltip>}
  </span>
)

interface Values {
  allowTestAccounts: boolean
  client: Client
  clientDetailed: ClientDetailedDto
  tickmillCompany: TickmillCompany
  introducedBy: { id: string; name: string | null } | null
  profile: IBProfile
  status: IntroducingBrokerStatus
  commissionCalculationStatus: { id: number; name: string }
  defaultProgramRegisteredAt: string | null
  registeredAt: string
  multiLevelRegisteredAt: string | null
  multiTierRegisteredAt: string | null
  multiLevelPercentage: number | null
  standardCommissions: StandardCommission[]
  assigneeId: string | null
  assigneeStatusId: number | null
  ibUserGetsClients: boolean
  loyaltyProgramEnabled: boolean
  loyaltyProgramEligible: boolean
  loyaltyDetails?: LoyaltyDetails
  restrictions: NameDto[]
  sibAssigneeId: string | null
  sibAssigneeStatusId: number | null
  sibIsAutoAssigned: boolean
  activeClients?: IntroducingBrokerLoyaltyDto['activeClients']
  volumeRatio?: IntroducingBrokerLoyaltyDto['volumeRatio']
  parentType: NameDto | null
}

const PER_SPREAD_SHARE_CALCULATION_METHOD_ID = 4

const IntroducingBrokerDetailsFormUI = (props: FormikProps<Values> & OuterProps) => {
  const { setFieldValue, values, introducingBroker, commissionPlans, assignedCommissionPlans } =
    props

  const [isEdit, setEdit] = useState<boolean>(false)

  const [instrumentCommissionModal, setInstrumentCommissionModal] = useState<{
    data?: StandardCommission
    visible: boolean
  }>({
    visible: false,
  })

  const partnershipsApiClient = useApiClient(PartnershipsApiClient)

  const [commissionPlanModal, setCommissionPlanModal] =
    useState<IntroducingBrokerCommissionPlanGroupDto | null>(null)

  const instrumentTypes = useAppSelector(
    (state) => state.tradingAccounts.tradingAccountInstrumentType
  )
  const platformTypes = useAppSelector((state) => state.tradingAccounts.platformTypes)
  const rebateCalcMethods = useAppSelector((state) => state.partnerships.rebateCalcMethods)
  const filteredRebateCalcMethods = rebateCalcMethods.filter(
    (rebateCalcMethod) => rebateCalcMethod.id !== PER_SPREAD_SHARE_CALCULATION_METHOD_ID
  )
  const instruments = useAppSelector((state) => state.tradingAccounts.instruments)

  const introducingBrokersByCompany =
    useAppSelector(
      (state) =>
        state.partnerships.introducingBrokersByCompany[props.clientDetailed.tickmillCompany.id]
    ) ?? []

  // console.log(
  //   state.partnerships.introducingBrokersByCompany,
  //   'state.partnerships.introducingBrokersByCompany'
  // )
  const introducingBrokerStatus =
    useAppSelector((state) => state.partnerships.introducingBrokerStatus) ?? []
  const introducingBrokerCalculationStatus =
    useAppSelector((state) => state.partnerships.introducingBrokerCalculationStatus) ?? []
  const introducingBrokersProfiles =
    useAppSelector((state) => state.partnerships.introducingBrokersProfiles) ?? []
  const introducingBrokerProfileAccountType =
    useAppSelector((state) => state.partnerships.introducingBrokerProfileAccountType) ?? []
  const users = useAppSelector((state) => state.identity.administrationUsers) ?? []
  const introducingBrokerParentTypes =
    useAppSelector((state) => state.partnerships.introducingBrokerParentTypes) ?? []

  const userOptions = useMemo(
    () =>
      users.map((item) => ({
        ...item,
        rightAddon: isUserStatusLocked(item?.status?.id) && (
          <CircleAlert aria-label='User Locked' className='text-destructive' />
        ),
      })),
    [users]
  )

  const restrictionOptions = useMemo(
    () =>
      props.restrictions.filter((restriction) =>
        CAMPAIGN_RESTRICTIONS_FIELD.includes(restriction.id)
      ),
    [props.restrictions]
  )

  const nextTierValue = values.loyaltyDetails?.nextTier
    ? `${values.loyaltyDetails.nextTier.name} at ${
        formatLargeNumber(values.loyaltyDetails.nextTier.minNotionalValue) ?? '-'
      } ${values.loyaltyDetails.nextTier.minNotionalValue != null ? 'Notional Value' : ''}`
    : '-'

  const isLoyaltyChoiceRestricted = Boolean(
    (values.restrictions || []).find(
      (x) => x.id === IntroducingBrokerRestrictions.RESTRICT_LOYALTY_CHOICE
    )
  )

  const handleAddInstrumentCommissions = () => {
    setInstrumentCommissionModal({ visible: true, data: undefined })
  }

  const handleEditInstrumentCommissions = (data: StandardCommission) => {
    setInstrumentCommissionModal({ visible: true, data })
  }

  const handleChangeInstrumentCommission = async (data: StandardCommission) => {
    let standardCommissions: StandardCommission[] = []

    if (instrumentCommissionModal.data) {
      standardCommissions = [
        ...values.standardCommissions.filter((x) => x !== instrumentCommissionModal.data),
        data,
      ]
    } else {
      standardCommissions = [...values.standardCommissions, data]
    }

    setFieldValue('standardCommissions', standardCommissions)
    setInstrumentCommissionModal({ visible: false, data: undefined })

    await partnershipsApiClient.updateIntroducingBrokerInstrumentCommissions(introducingBroker.id, {
      InstrumentCommissions: standardCommissions as StandardCommissionDto[],
    })

    props.onHandleReloadGrid()
  }

  const handleDeleteInstrumentCommission = async (data: StandardCommission) => {
    const standardCommissions: StandardCommission[] = [
      ...values.standardCommissions.filter((x) => x !== data),
    ]

    setFieldValue('standardCommissions', standardCommissions)

    await partnershipsApiClient.updateIntroducingBrokerInstrumentCommissions(introducingBroker.id, {
      InstrumentCommissions: standardCommissions as StandardCommissionDto[],
    })

    props.onHandleReloadGrid()
  }

  const handleEditCommissionPlan = (id?: string) => {
    const dataToEdit = commissionPlans.find((x) => x.id === id)

    if (dataToEdit) {
      setCommissionPlanModal({
        ...dataToEdit,
      })
    } else {
      setCommissionPlanModal({
        id: '',
        name: '',
        isDefault: false,
        commissionPlans: [],
      })
    }
  }

  const handleAddUpdateCommissionPlanGroup = async (
    dataItem: AddEditIntroducingBrokerCommissionPlanGroupDtoFormValues[],
    id?: string
  ) => {
    await props.onUpdateCommissionPlan(dataItem, id)
  }

  const handleAssigneeField = (value: string | number) => {
    const user = users.find((x) => x.id === value)

    if (user) {
      setFieldValue('assigneeId', user.id)

      if (values.sibIsAutoAssigned && !values.sibAssigneeId) {
        setFieldValue('sibAssigneeId', user.id)
      }

      if (isUserStatusLocked(user.status?.id)) {
        setFieldValue('sibIsAutoAssigned', false)
      }
    } else {
      setFieldValue('assigneeId', null)
      setFieldValue('ibUserGetsClients', false)
    }
  }

  const handleSibAssigneeField = (value: string | number) => {
    const user = users.find((x) => x.id === value)

    if (user) {
      setFieldValue('sibAssigneeId', user.id)
    } else {
      setFieldValue('sibAssigneeId', null)
    }
  }

  useEffect(() => {
    if (values.sibIsAutoAssigned && values.assigneeId && !values.sibAssigneeId) {
      setFieldValue('sibAssigneeId', values.assigneeId)
    }
  }, [values.sibIsAutoAssigned, values.assigneeId, values.sibAssigneeId])

  const isSubIb = values.clientDetailed.mainIbScheme.id === MainIBSchemeEnum.MULTI_TIER
  const hasDefaultIBScheme = values.defaultProgramRegisteredAt

  return (
    <>
      <IntroducingBrokerNavigation
        data={props.introducingBroker}
        isEdit={isEdit}
        tab={IntroducingBrokerTab.DETAILS}
      />

      {instrumentCommissionModal.visible && (
        <Dialog
          open={instrumentCommissionModal.visible}
          onOpenChange={(open) =>
            !open && setInstrumentCommissionModal({ visible: false, data: undefined })
          }
        >
          <InstrumentCommissionModal
            instruments={instruments}
            instrumentTypes={instrumentTypes}
            platformTypes={platformTypes}
            rebateCalcMethods={filteredRebateCalcMethods}
            handleChange={handleChangeInstrumentCommission}
            introducingBrokerProfileAccountType={introducingBrokerProfileAccountType}
            data={instrumentCommissionModal.data}
          />
        </Dialog>
      )}

      {commissionPlanModal && (
        <IntroducingBrokerRebateFormModal
          data={commissionPlanModal}
          assignedCommissionPlans={assignedCommissionPlans}
          onSubmit={handleAddUpdateCommissionPlanGroup}
          onClose={() => setCommissionPlanModal(null)}
        />
      )}

      <DataGrid rightAddon={<ActionButtons {...props} isEdit={isEdit} setEdit={setEdit} />}>
        <DataColumn>
          <DataCard columns={2} title='Main Information'>
            <FormReadonlyField
              label='Client'
              value={values.client.name ?? ''}
              link={generatePath(links.recordsClients.detailsUrl as string, {
                id: values.client.id,
                slug: 'details',
              })}
            />

            <FormCheckboxField
              name='allowTestAccounts'
              label='Allow test accounts'
              readOnly={!isEdit}
            />

            <FormSelectField
              options={introducingBrokersByCompany}
              label={
                <CacheLabel
                  label='Introduced By'
                  message={INTRODUCING_BROKERS_CACHE_MESSAGE}
                  ariaLabel='Introducing Broker cache information'
                />
              }
              allowClear
              showSearch
              name='introducedBy'
              readOnly={!isEdit}
              disabled={
                !hasPermission(props.permissions, PermissionsEnum.CanEditIntroducingBroker) ||
                !introducingBroker.defaultProgramRegisteredAt
              }
              link={
                values.introducedBy
                  ? generatePath(links.recordsIntroducingBrokers.detailsUrl as string, {
                      id: values.introducedBy?.id,
                      slug: 'details',
                    })
                  : undefined
              }
            />

            <FormSelectField
              options={introducingBrokerParentTypes}
              label='Parent Type'
              allowClear
              showSearch
              name='parentType'
              readOnly={!isEdit}
            />

            <FormSelectField
              options={introducingBrokersProfiles}
              label='Profile'
              allowClear
              showSearch
              name='profile'
              readOnly={!isEdit}
              link={
                values.profile.id
                  ? generatePath(links.brokerageIBProfiles.detailsUrl as string, {
                      id: values.profile.id,
                      slug: 'details',
                    })
                  : undefined
              }
            />

            <FormSelectField
              name='status.id'
              label='Status'
              options={introducingBrokerStatus}
              readOnly={!isEdit}
            />

            <FormSelectField
              name='commissionCalculationStatus.id'
              label='Comm.Calc.Status'
              options={introducingBrokerCalculationStatus}
              readOnly={!isEdit}
            />

            <FormReadonlyField
              label='Registered At'
              value={formatLocalDateUTC(values.registeredAt, true)?.toString()}
            />
          </DataCard>

          <DataCard columns={2} title='Assignment'>
            <FormSelectField
              options={userOptions}
              label='Assignee'
              name='assigneeId'
              placeholder='Assignee'
              showSearch
              allowClear
              onChange={handleAssigneeField}
              readOnly={!isEdit}
            />

            <FormCheckboxField
              name='ibUserGetsClients'
              label='Assignee cascading to clients'
              readOnly={!isEdit}
            />

            <FormSelectField
              options={userOptions}
              label='SIB Assignee'
              name='sibAssigneeId'
              placeholder='SIB Assignee'
              showSearch
              allowClear
              onChange={handleSibAssigneeField}
              readOnly={!isEdit}
            />

            <FormCheckboxField
              name='sibIsAutoAssigned'
              label='SIB Auto Assignment'
              readOnly={!isEdit}
            />
          </DataCard>

          <DataCard title='Restrictions'>
            <FormMultiSelectField
              options={restrictionOptions}
              label='Restrictions'
              showSearch
              readOnly={!isEdit}
              name='restrictions'
            />
          </DataCard>

          <DataCard columns={2} title='Multi Level Details'>
            <FormDateField
              name='multiLevelRegisteredAt'
              label='Registered At'
              readOnly={!isEdit}
              showTimeSelect
              dateFormatType={getDateTimeFormat()}
              disabled={isSubIb || !hasDefaultIBScheme}
            />

            <FormField
              name='multiLevelPercentage'
              label={
                <DetailFieldLabel
                  label='Percentage'
                  tooltip={
                    isSubIb && (
                      <span>
                        Multi-Level details cannot be set for client under Multi-Tier scheme
                      </span>
                    )
                  }
                />
              }
              type='number'
              readOnly={!isEdit}
              disabled={isSubIb || !hasDefaultIBScheme}
            />
          </DataCard>

          {values.multiTierRegisteredAt && (
            <DataCard title='Multi Tier Details'>
              <FormReadonlyField
                label='Registered At'
                value={formatLocalDateUTC(values.multiTierRegisteredAt, true)?.toString()}
              />
            </DataCard>
          )}
        </DataColumn>

        <DataColumn>
          <DataCard columns={2} title='IB Bonus Program'>
            <FormReadonlyField
              label='Program Rules'
              value='Open Document'
              readOnly
              link={{ target: '_blank', to: LOYALTY_PROGRAM_RULES_URL }}
            />

            <FormCheckboxField
              name='loyaltyProgramEligible'
              label={
                <DetailFieldLabel
                  label='Loyalty Rewards Eligible'
                  tooltip={
                    isEdit && !values.loyaltyProgramEnabled ? (
                      <span>To enable this flag, loyalty must be enabled first.</span>
                    ) : undefined
                  }
                />
              }
              readOnly={!isEdit}
              disabled={!values.loyaltyProgramEnabled}
            />

            <FormCheckboxField
              name='loyaltyProgramEnabled'
              label='Loyalty Enabled'
              readOnly={!isEdit}
              onChange={(checked) => {
                if (!checked) setFieldValue('loyaltyProgramEligible', false)
                setFieldValue('loyaltyProgramEnabled', checked)
              }}
            />

            <FormCheckboxField
              label={
                <DetailFieldLabel
                  label='Restrict Loyalty Choice'
                  tooltip={<span>This check-box is for disabling automation for this IB</span>}
                />
              }
              value={isLoyaltyChoiceRestricted}
              readOnly={!isEdit}
              onChange={(checked) => {
                if (!checked) {
                  setFieldValue(
                    'restrictions',
                    values.restrictions.filter(
                      (x) => x.id !== IntroducingBrokerRestrictions.RESTRICT_LOYALTY_CHOICE
                    )
                  )
                  return
                }

                setFieldValue('restrictions', [
                  ...values.restrictions.filter(
                    (x) => x.id !== IntroducingBrokerRestrictions.RESTRICT_LOYALTY_CHOICE
                  ),
                  {
                    id: IntroducingBrokerRestrictions.RESTRICT_LOYALTY_CHOICE,
                    name: 'Restrict Loyalty Choice',
                  },
                ])
              }}
            />

            <FormReadonlyField
              label='Notional Value'
              value={formatNumberWithCommas(values.loyaltyDetails?.bonusNv ?? 0, {
                minDecimalPlaces: 0,
              })}
            />

            <FormReadonlyField
              label='Current Tier'
              value={values.loyaltyDetails?.currentTier?.name ?? ''}
            />

            <FormReadonlyField label='Next Tier' value={nextTierValue} />

            <FormReadonlyField
              label='Current Active Clients'
              value={values.activeClients?.current ?? ''}
            />

            <FormReadonlyField
              label='Required Active Clients'
              value={values.activeClients?.required ?? ''}
            />

            <FormReadonlyField
              label='Remaining Active Clients'
              value={values.activeClients?.remaining ?? ''}
            />

            <FormReadonlyField
              label='Current Volume Ratio'
              value={values.volumeRatio?.current ?? ''}
            />

            <FormReadonlyField
              label='Required Volume Ratio'
              value={values.volumeRatio?.required ?? ''}
            />

            <FormReadonlyField
              label='Remaining Volume Ratio'
              value={values.volumeRatio?.remaining ?? ''}
            />
          </DataCard>

          <DataCard title='Commission Exceptions'>
            <div className='flex flex-col gap-3'>
              <div>
                <AddButton disabled={!isEdit} text='Add' onClick={handleAddInstrumentCommissions} />
              </div>

              <InstrumentCommissionsGrid
                standardCommissions={values.standardCommissions}
                isEdit={isEdit}
                setFieldValue={setFieldValue}
                handleEditStandardCommissions={handleEditInstrumentCommissions}
                tableData={props.tableData}
                onHandleGridPageChange={props.onHandleGridPageChange}
                onHandleDeleteInstrumentCommission={handleDeleteInstrumentCommission}
              />
            </div>
          </DataCard>

          {values.multiTierRegisteredAt && (
            <DataCard title='Commission Plan'>
              <Grid
                resizable
                data={assignedCommissionPlans?.commissionPlans.map((x) => ({
                  platformType: x.tradingAccountType.name,
                  amount: x.amount,
                }))}
              >
                <Column field='platformType' title='Platform Type' />
                <Column field='amount' title='Max rebate available' />
              </Grid>
            </DataCard>
          )}

          {values.multiTierRegisteredAt && shouldDisplayTable(commissionPlans) && (
            <DataCard title='IB Commission Plans'>
              <div className='flex flex-col gap-3'>
                <div>
                  <AddButton
                    disabled={!isEdit || shouldDisableAddNewButton(commissionPlans)}
                    text='Add new IB Commission Plan'
                    onClick={handleEditCommissionPlan}
                  />
                </div>

                <Grid
                  resizable
                  data={[...commissionPlans]
                    .map((x: IntroducingBrokerCommissionPlanGroupDto) => ({
                      id: x.id,
                      plan: x.name,
                      isDefault: x.isDefault,
                      classicAmount:
                        x.commissionPlans.find(
                          (plan) => plan.tradingAccountType.id === TradingAccountType.Classic
                        )?.amount ?? 0,
                      proAmount:
                        x.commissionPlans.find(
                          (plan) => plan.tradingAccountType.id === TradingAccountType.Pro
                        )?.amount ?? 0,
                      vipAmount:
                        x.commissionPlans.find(
                          (plan) => plan.tradingAccountType.id === TradingAccountType.VIP
                        )?.amount ?? 0,
                      rawAmount:
                        x.commissionPlans.find(
                          (plan) => plan.tradingAccountType.id === TradingAccountType.Raw
                        )?.amount ?? 0,
                    }))
                    .sort((a, b) =>
                      a.isDefault && !b.isDefault ? -1 : !a.isDefault && b.isDefault ? 1 : 0
                    )}
                >
                  <Column field='plan' title='Profiles' />
                  <Column field='classicAmount' title='Classic' />
                  <Column field='vipAmount' title='VIP' />
                  <Column field='proAmount' title='Pro' />
                  <Column field='rawAmount' title='Raw' />
                  <Column
                    width={50}
                    minResizableWidth={50}
                    title=''
                    cell={(gridCellProps: GridCellProps) => (
                      <td>
                        <MoreButton
                          items={[
                            {
                              name: 'Edit',
                              disabled: !isEdit || gridCellProps.dataItem.isDefault,
                              onClick: () => handleEditCommissionPlan(gridCellProps.dataItem.id),
                            },
                          ]}
                        />
                      </td>
                    )}
                  />
                </Grid>
              </div>
            </DataCard>
          )}
        </DataColumn>
      </DataGrid>
    </>
  )
}

function shouldDisplayTable(commissionPlans: IntroducingBrokerCommissionPlanGroupDto[]): boolean {
  return !!commissionPlans.length
}

function shouldDisableAddNewButton(
  commissionPlans: IntroducingBrokerCommissionPlanGroupDto[]
): boolean {
  return commissionPlans.filter((x) => !x.isDefault).length > 2
}

interface OuterProps {
  tableData: TableStateContextData
  standardCommissions: StandardCommission[]
  introducingBroker: IntroducingBrokerDetailsDto
  clientDetailed: ClientDetailedDto
  assignedCommissionPlans: IntroducingBrokerCommissionPlanGroupDto | undefined
  commissionPlans: IntroducingBrokerCommissionPlanGroupDto[]
  permissions: PermissionDto[]
  platformTypes: PlatformTypeDto[]
  rebateCalcMethods: NameDto[]
  bonusNvAdjustmentReasons: NameDto[]
  restrictions: NameDto[]

  onSubmit(values: FormSubmitValues<UpdateIntroducingBroker>): Promise<void>
  onUpdateCommissionPlan(
    data: AddEditIntroducingBrokerCommissionPlanGroupDtoFormValues[],
    id?: string
  ): void

  setAuditLogModal(value: boolean): void
  setIbBonusModal(value: boolean): void
  onHandleGridPageChange(pageNumber: number): void
  onHandleReloadGrid(): void
}

export const IntroducingBrokerForm = withFormik<OuterProps, Values>({
  mapPropsToValues: (props) => {
    const { introducingBroker, clientDetailed } = props

    return {
      allowTestAccounts: introducingBroker.allowTestAccounts,
      clientDetailed,
      client: introducingBroker.client,
      tickmillCompany: introducingBroker.client.tickmillCompany,
      introducedBy: introducingBroker.parent || null,
      profile: introducingBroker.profile,
      status: introducingBroker.status,
      commissionCalculationStatus: introducingBroker.commissionCalculationStatus,
      defaultProgramRegisteredAt: introducingBroker.defaultProgramRegisteredAt,
      registeredAt: introducingBroker.registeredAt,
      multiLevelRegisteredAt: introducingBroker.multiLevelRegisteredAt,
      multiTierRegisteredAt: introducingBroker.multiTierRegisteredAt,
      multiLevelPercentage: introducingBroker.multiLevelPercentage,
      parentType: introducingBroker.parentType,
      standardCommissions: props.standardCommissions,
      assigneeId: introducingBroker.assignee?.id || null,
      assigneeStatusId: introducingBroker.assignee?.status?.id || null,
      ibUserGetsClients: introducingBroker.ibUserGetsClients,
      loyaltyProgramEnabled: introducingBroker.loyaltyProgramEnabled,
      loyaltyProgramEligible: introducingBroker.loyaltyProgramEligible,
      loyaltyDetails: introducingBroker.loyaltyDetails,
      restrictions: introducingBroker.restrictions || [],
      sibAssigneeId: introducingBroker.sibAssignee?.id || null,
      sibAssigneeStatusId: introducingBroker.sibAssignee?.status?.id || null,
      sibIsAutoAssigned: introducingBroker.sibIsAutoAssigned,
      activeClients: introducingBroker.activeClients,
      volumeRatio: introducingBroker.volumeRatio,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    const data: UpdateIntroducingBroker = {
      allowTestAccounts: values.allowTestAccounts,
      clientId: props.introducingBroker.client.id,
      id: props.introducingBroker.id,
      commissionCalculationStatusId: values.commissionCalculationStatus.id,
      multiLevelPercentage: values.multiLevelPercentage,
      multiLevelRegisteredAt: values.multiLevelRegisteredAt ?? null,
      multiTierRegisteredAt: values.multiTierRegisteredAt ?? null,
      name: props.introducingBroker.name,
      parentIntroducingBrokerId: values.introducedBy ? values.introducedBy.id : null,
      statusId: values.status.id,
      profileId: values.profile.id,
      loyaltyProgramEnabled: values.loyaltyProgramEnabled,
      loyaltyProgramIsEligible: values.loyaltyProgramEligible,
      assigneeId: values.assigneeId || null,
      ibUserGetsClients: values.ibUserGetsClients,
      restrictions: (values.restrictions || []).map((x) => Number(x.id)),
      sibAssigneeId: values.sibAssigneeId || null,
      sibAssigneeStatusId: values.sibAssigneeStatusId || null,
      sibIsAutoAssigned: values.sibIsAutoAssigned,
      introducingBrokerParentTypeId: values.parentType ? values.parentType.id : null,
    }

    await props.onSubmit(data)
    setSubmitting(false)
  },
  enableReinitialize: true,
})(IntroducingBrokerDetailsFormUI)

interface Props {
  data: IntroducingBrokerDetailsDto
  clientDetailed: ClientDetailedDto
  forceRefresh(): void
}

const IntroducingBrokerDetailsPage: React.FC<Props> = ({ data, clientDetailed, forceRefresh }) => {
  const [auth] = useContext(AuthSessionContext)
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const partnershipsApiClient = useApiClient(PartnershipsApiClient)
  const identityApiClient = useApiClient(IdentityApiClient)

  const [loading, setLoading] = useState<boolean>(false)
  const [auditLogVisible, setAuditLogVisible] = useState(false)
  const [ibBonusModal, setIbBonusModal] = useState(false)
  const [assignedCommissionPlans, setAssignedCommissionPlans] =
    useState<IntroducingBrokerCommissionPlanGroupDto>()
  const [commissionPlans, setCommissionPlans] = useState<IntroducingBrokerCommissionPlanGroupDto[]>(
    []
  )

  const introducingBrokerStatus = useAppSelector(
    (state) => state.partnerships.introducingBrokerStatus
  )
  const introducingBrokerCalculationStatus = useAppSelector(
    (state) => state.partnerships.introducingBrokerCalculationStatus
  )
  const introducingBrokerProfileAccountType = useAppSelector(
    (state) => state.partnerships.introducingBrokerProfileAccountType
  )
  const introducingBrokerParentTypes = useAppSelector(
    (state) => state.partnerships.introducingBrokerParentTypes
  )
  const platformTypes = useAppSelector((state) => state.tradingAccounts.platformTypes)
  const bonusNvAdjustmentReasons = useAppSelector(
    (state) => state.partnerships.bonusNvAdjustmentReasons
  )
  const instruments = useAppSelector((state) => state.tradingAccounts.instruments)
  const users = useAppSelector((state) => state.identity.administrationUsers)
  const permissions = auth?.permissions || []
  const rebateCalcMethods = useAppSelector((state) => state.partnerships.rebateCalcMethods)
  const tradingAccountRestrictions = useAppSelector((state) => state.tradingAccounts.restrictions)

  const dispatch = useAppDispatch()

  const [standardCommissionsState, setStandardCommissions] = useState<StandardCommission[]>([])
  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: false })
  const { gridState, gridActions } = useGridState()
  const { gridStatusActions } = useGridStatusState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)
  const [reloadGrid, setReloadGrid] = useState(false)

  const handleReloadGrid = () => setReloadGrid((prv) => !prv)

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      return buildQuery(filter ?? gridState.filter) ?? null
    },
    [gridState.filter]
  )

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (gridFilterState: GridFilterActionState): Promise<PageData<StandardCommission[]>> => {
          const searchQuery = getQuery(gridFilterState.filter)
          const response = await partnershipsApiClient.getIntroducingBrokerInstrumentCommissions(
            data.id,
            {
              search: searchQuery,
              pageIndex: gridFilterState.pageIndex,
              pageSize: gridFilterState.take,
              skipCount: tableData?.skipCount !== false,
            }
          )

          if (response?.items) {
            setStandardCommissions(response.items)
          }

          setTableData({ ...tableData, meta: response })

          return response
        }
      )
    })()
  }, [reloadGrid]) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<StandardCommission[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )

    const response = await callback(gridFilterState)

    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        take: gridFilterState.take,
        skip: gridFilterState.skip,
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const handleGridPageChange = async (skip: number) => {
    const pageIndex = skip / gridState.pageSize + 1
    const searchQuery = getQuery()

    await gridActions.setGridFilters({
      ...gridState,
      skip,
      pageIndex,
    })

    const response = await partnershipsApiClient.getProfileStandardCommissions(data.id, {
      search: searchQuery,
      pageSize: gridState.pageSize,
      skip,
      pageIndex,
      skipCount: tableData?.skipCount !== false,
    })

    await gridActions.setGridFilters({
      ...gridState,
      skip,
      pageIndex,
      total: response.totalResults,
    })

    if (response?.items) {
      setStandardCommissions(response.items)
    }

    setTableData({ ...tableData, meta: response })
  }

  useEffect(() => {
    const fetchIntroducingBrokerDetails = async () => {
      try {
        setLoading(true)

        dispatch(
          fetchIntroducingBrokerByTickmillCompany(
            partnershipsApiClient,
            data.client.tickmillCompany.id
          )
        )

        if (isEmpty(platformTypes)) dispatch(fetchPlatformTypesAction(tradingAccountsApiClient))
        if (isEmpty(bonusNvAdjustmentReasons))
          dispatch(fetchBonusNvAdjustmentReasons(partnershipsApiClient))
        if (isEmpty(rebateCalcMethods))
          dispatch(fetchRebateCalcMethodTypesAction(partnershipsApiClient))
        if (isEmpty(introducingBrokerStatus)) {
          dispatch(fetchIntroducingBrokerStatusesAction(partnershipsApiClient))
        }
        if (isEmpty(introducingBrokerCalculationStatus)) {
          dispatch(fetchIntroducingBrokerCalculationStatusesAction(partnershipsApiClient))
        }
        if (isEmpty(introducingBrokerProfileAccountType)) {
          dispatch(fetchIntroducingBrokerProfileAccountTypeAction(partnershipsApiClient))
        }
        if (isEmpty(introducingBrokerParentTypes)) {
          dispatch(fetchIntroducingBrokerParentTypesAction(partnershipsApiClient))
        }
        if (isEmpty(instruments)) {
          dispatch(fetchInstrumentsAction(tradingAccountsApiClient))
        }
        if (isEmpty(users)) {
          dispatch(fetchAdministrationUsersAction(identityApiClient))
        }
        if (isEmpty(tradingAccountRestrictions)) {
          dispatch(fetchTradingAccountRestrictions(tradingAccountsApiClient))
        }

        setLoading(false)
      } catch (e) {
        console.error(e)
        setLoading(false)
      }
    }

    fetchIntroducingBrokerDetails()
  }, [data.id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (data) {
      dispatch(
        fetchIntroducingBrokerProfilesByTickmillCompany(
          partnershipsApiClient,
          data.client.tickmillCompany.id
        )
      )

      if (data.multiTierRegisteredAt) {
        partnershipsApiClient
          .getAssignedIntroducingBrokerCommissionPlanGroups(data.id)
          .then((r) => {
            setAssignedCommissionPlans(r)
          })

        if (!data.isLastMultiTierLevel) {
          partnershipsApiClient.getIntroducingBrokerCommissionPlanGroups(data.id).then((r) => {
            setCommissionPlans(r)
          })
        }
      }
    }
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  const setToast = useContext(ToastContext)

  const onSubmit = async (values: UpdateIntroducingBroker) => {
    try {
      await partnershipsApiClient.updateIntroducingBroker(values)
      setToast(successToast('Introducing Broker updated successfully.'))
    } catch (e) {
      console.error(e)
    } finally {
      forceRefresh()
    }
  }

  const onUpdateCommissionPlanGroup = async (
    values: AddEditIntroducingBrokerCommissionPlanGroupDtoFormValues[],
    id?: string
  ) => {
    try {
      if (id) {
        await partnershipsApiClient.updateIntroducingBrokerCommissionPlanGroups(data.id, id, values)
        setToast(successToast('Introducing Broker Commission Plan updated successfully.'))
      } else {
        await partnershipsApiClient.addIntroducingBrokerCommissionPlanGroups(data.id, values)
        setToast(successToast('Introducing Broker Commission Plan added successfully.'))
      }

      forceRefresh()
    } catch (e) {
      console.error(e)
    }
  }

  const handleLoyaltyRewardsSubmit = async (values: AdjustNotionalValue) => {
    await partnershipsApiClient.updateIntroducingBrokerBonusNv(values)
    setToast(successToast('Introducing Broker Notional Value updated successfully.'))
    forceRefresh()
  }

  if (loading) {
    return (
      <div className='flex min-h-80 items-center justify-center'>
        <Spinner className='size-6' />
      </div>
    )
  }

  if (!data) {
    return null
  }

  return (
    <>
      {auditLogVisible && (
        <ClientAreaAuditLogModal
          isVisible={auditLogVisible}
          onClose={() => setAuditLogVisible(false)}
          introducingBrokerId={data.id}
        />
      )}

      {ibBonusModal && (
        <IntroducingBrokerLoyaltyProgramForm
          isOpen={ibBonusModal}
          introducingBroker={data}
          bonusNvAdjustmentReasons={bonusNvAdjustmentReasons}
          onSubmit={handleLoyaltyRewardsSubmit}
          onCancel={() => setIbBonusModal(false)}
        />
      )}

      <IntroducingBrokerForm
        setAuditLogModal={setAuditLogVisible}
        introducingBroker={data}
        clientDetailed={clientDetailed}
        assignedCommissionPlans={assignedCommissionPlans}
        commissionPlans={commissionPlans}
        bonusNvAdjustmentReasons={bonusNvAdjustmentReasons}
        platformTypes={platformTypes}
        rebateCalcMethods={rebateCalcMethods}
        onSubmit={onSubmit}
        onUpdateCommissionPlan={onUpdateCommissionPlanGroup}
        permissions={permissions}
        setIbBonusModal={setIbBonusModal}
        restrictions={tradingAccountRestrictions}
        tableData={tableData}
        standardCommissions={standardCommissionsState}
        onHandleGridPageChange={handleGridPageChange}
        onHandleReloadGrid={handleReloadGrid}
      />
    </>
  )
}

const ActionButtons = (
  props: FormikProps<Values> & OuterProps & { isEdit: boolean; setEdit: (value: boolean) => void }
) => {
  const { isEdit, setEdit, isValid, isSubmitting, resetForm, submitForm, permissions } = props

  const cancel = () => {
    setEdit(false)
    resetForm()
  }

  const save = () => {
    setEdit(false)
    submitForm()
  }

  return (
    <ButtonGroup>
      {isEdit ? (
        <>
          <Button variant='outline' onClick={cancel} data-test='cancel-button'>
            Cancel
          </Button>

          <Button disabled={!isValid || isSubmitting} onClick={save} data-test='save-button'>
            Save
            {isSubmitting && <Spinner data-icon='inline-start' />}
          </Button>
        </>
      ) : (
        <Button
          onClick={() => setEdit(true)}
          disabled={!hasPermission(permissions, PermissionsEnum.CanEditIntroducingBroker)}
          data-test='edit-button'
        >
          Edit
        </Button>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant='outline'
              size='icon'
              aria-label='More options'
              data-test='more-button'
              disabled={isSubmitting}
            />
          }
        >
          <ChevronDown />
        </DropdownMenuTrigger>

        <DropdownMenuContent className='min-w-max' align='end'>
          <DropdownMenuItem
            onSelect={() => props.setAuditLogModal(true)}
            data-test='audit-log-menu-button'
          >
            <ScrollText />
            Audit Log
          </DropdownMenuItem>

          {hasPermission(permissions, PermissionsEnum.CanEditIntroducingBrokerLoyaltyPoints) && (
            <DropdownMenuItem
              onSelect={() => props.setIbBonusModal(true)}
              data-test='ib-bonus-menu-button'
            >
              IB Bonus Adjustment
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </ButtonGroup>
  )
}

export default IntroducingBrokerDetailsPage
